// 异步调用后台接口
import axios from 'axios';
import { stringify } from 'qs';
import AppCore from '@/appcore';
import { getValueFromUA } from '@/asset/js/utils';

/**
 * 修后端返回的非法 JSON：字段值（引号里）如果直接敲了回车/换行/Tab，JSON.parse 会失败。
 * 这里只把「引号里面」的真实换行改成 \n、\r、\t，结构外面的换行不动。
 * 例如 "reason":"ceshi⏎" → "reason":"ceshi\n"
 */
function sanitizeJsonStringLiterals(text) {
  if (typeof text !== 'string' || !text) return text;
  let out = '';
  let inString = false;
  let escaped = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (escaped) {
        out += ch;
        escaped = false;
        continue;
      }
      if (ch === '\\') {
        out += ch;
        escaped = true;
        continue;
      }
      if (ch === '"') {
        out += ch;
        inString = false;
        continue;
      }
      if (ch === '\n') {
        out += '\\n';
        continue;
      }
      if (ch === '\r') {
        out += '\\r';
        continue;
      }
      if (ch === '\t') {
        out += '\\t';
        continue;
      }
      out += ch;
      continue;
    }
    if (ch === '"') {
      inString = true;
    }
    out += ch;
  }
  return out;
}

/** 先正常 JSON.parse；失败再修引号内的裸换行后重试 */
function parseEncryptJson(text) {
  if (typeof text !== 'string') return text;
  const trimmed = text.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch (e) {
    try {
      return JSON.parse(sanitizeJsonStringLiterals(trimmed));
    } catch (e2) {
      return null;
    }
  }
}

/** 解析加密接口响应：支持已解密的 { code, message, body } 或 JSON 字符串 */
function unwrapEncryptApiPayload(payload) {
  if (payload == null) return null;
  let parsed = payload;
  if (typeof payload === 'string') {
    parsed = parseEncryptJson(payload);
    if (parsed == null) return null;
  }
  if (typeof parsed !== 'object') return null;
  if (parsed.body !== undefined) {
    return {
      data: parsed.body,
      error: parsed.message || '',
      code: parsed.code
    };
  }
  return null;
}

let _encryptRequestChain = Promise.resolve();

export const fetchData = async (method, url, params, options, isEncrypt = false) => {
  try {
    let result = {};
    if (method === 'POST') {
      if (isEncrypt) {
        const postData = url.split('?data=');
        const decodedData = decodeURIComponent(postData[1]);
        const urls = postData[0];
        try {
          result = await axios.post(urls, decodedData, options);
        } catch (e) {
          console.log(e, 'e');
        }
      } else {
        result = await axios.post(url, { ...params }, options);
      }
    } else if (method === 'GET') {
      if (isEncrypt) {
        result = await axios.get(url, options);
      } else {
        result = await axios.get(`${url}?${stringify({ ...params })}`, options);
      }
    } else if (method === 'PUT') {
      result = await axios.put(url, { ...params }, options);
    }
    
    const { code, message, msg, body, data, headers } = result;
    const payload = body !== undefined ? body : data;
    
    if (code === 1 || code === '1') {
      return { data: payload, headers };
    }
    return { error: message || msg || 'ajax.system', code: code, data: payload, headers };
  } catch (error) {
    console.log(error);
    return { error: error };
  }
};

export const fetchDataWithEncrypt = async (method, url, params, options = {}) => {
  const run = () => fetchDataWithEncryptInternal(method, url, params, options);
  const resultPromise = _encryptRequestChain.then(run, run);
  _encryptRequestChain = resultPromise.catch(() => {});
  return resultPromise;
};

const fetchDataWithEncryptInternal = async (method, url, params, options = {}) => {
  try {    
    // 第一步：加密URL和参数
    const encryptResult = await AppCore.webEncryptUrl(params, url);
    // console.log('加密结果:', encryptResult);
    const { authKey, encryptedUrl, appHeader } = encryptResult;

    // Android: appHeader 是 JSON 对象/字符串，包含完整 HTTP headers，需要展开
    // iOS: appHeader 是 base64 加密 Token，作为 app-header 整体发送
    let appHeaderObj = {};
    let isAndroidHeaders = false;
    if (appHeader) {
      if (typeof appHeader === 'object') {
        appHeaderObj = appHeader;
        isAndroidHeaders = true;
      } else if (typeof appHeader === 'string' && appHeader.trimStart().startsWith('{')) {
        try {
          appHeaderObj = JSON.parse(appHeader);
          isAndroidHeaders = true;
        } catch (e) {
          console.warn('appHeader JSON 解析失败，已忽略:', e);
        }
      }
    }

    if (isAndroidHeaders) {
      // Android: 展开 JSON headers
      options.headers = {
        ...options.headers,
        ...appHeaderObj
      };
    } else if (authKey) {
      // iOS: 保持原始逻辑，authKey 作为 x-auth-key，appHeader 作为 app-header
      options.headers = {
        ...options.headers,
        'x-auth-key': authKey,
        'app-header': appHeader || ''
      };
    }

    if (method === 'POST' && !options.headers['Content-Type']) {
      options.headers['Content-Type'] = 'application/json';
    }
    
    // 第二步：发送加密请求
    // console.log('准备发送请求到:', encryptedUrl);
    const result = await fetchData(method, encryptedUrl, params, options, true);

    // 响应体可能已是明文 JSON（含 code/body），无需 native 解密
    const plainResult = unwrapEncryptApiPayload(result.data);
    if (plainResult) {
      return plainResult;
    }

    // 第三步：解密响应数据
    // 优先用响应头返回的 x-auth-key，没有则回退到 webEncryptUrl 返回的 authKey(短AES密钥)
    const xAuthKey = result.headers?.['x-auth-key'] || result.headers?.['X-Auth-Key'] || authKey;

    if (xAuthKey && result?.data && typeof result.data === 'string') {
      try {
        const decryptedData = await AppCore.webDecretedData(xAuthKey, result.data);
        console.log(decryptedData, 'decryptedData');

        if (decryptedData) {
          const dtParsed = unwrapEncryptApiPayload(decryptedData);
          if (dtParsed) {
            return dtParsed;
          }
          try {
            // 解密结果可能因字段值含真实换行而非法，parseEncryptJson 会自动修后再解析
            const dt = parseEncryptJson(decryptedData);
            if (!dt) {
              throw new Error('JSON parse failed after sanitize');
            }
            return {
              data: dt.body !== undefined ? dt.body : dt,
              error: dt.message || '',
              code: dt.code
            };
          } catch (parseError) {
            console.error('解密数据 JSON 解析失败:', parseError);
          }
        }
      } catch (decryptError) {
        console.error('解密失败:', decryptError);
      }
    }

    // 解密失败时再次尝试直接解析原始 data
    const fallbackResult = unwrapEncryptApiPayload(result.data);
    if (fallbackResult) {
      return fallbackResult;
    }

    if (result.data && !result.error) {
      return {
        data: result.data,
        error: '',
        code: result.code ?? 1
      };
    }

    return {
      data: result.data,
      error: result.error,
      code: result.code
    };
    
  } catch (error) {
    console.error('fetchDataWithEncrypt error:', error);
    
    // 如果加密流程失败，降级到普通的 fetchData
    try {
      console.log('降级到普通请求:', url);
      return await fetchData(method, url, params, options);
    } catch (fallbackError) {
      console.error('降级请求也失败:', fallbackError);
      return { error: error.message || '请求失败', data: null };
    }
  }
};

export async function uploadImage(params) {
  return await axios.post('/api/cms/image', params, {
    headers: {
      adminName: 'wap'
    }
  });
}

const platform = getValueFromUA('appPlatform') === 'android' ? 1 : 2;
const appPlatform = getValueFromUA('appPlatform') === 'android' ? 1 : 2;

export const getUserInfoById = async params =>
  fetchData('GET', '/api/user/snackUser/getUserInfoById', params);

export const getShareUserInfoById = async params =>
  fetchData('GET', '/api/user/snackUser/share/userInfo', params);

export const getRoomInfo = async params =>
  fetchData('GET', '/api/voice/content/room/share/info', params);

export const feedback = async params => fetchData('POST', '/api/cms/feedback/insert', params);

export const reportRoom = async params => fetchData('POST', '/api/cms/roomReport/insert', params);

export const reportUser = async params => fetchData('POST', '/api/cms/userReport/insert', params);

export const fetchGiftWallList = async params =>
  fetchData('GET', '/api/voice/user/userAssets/getGiftWallInfo', params);

export const fetchGiftWallValue = async params =>
  fetchData('GET', '/api/voice/user/userAssets/getCoinMinValue', params);

export const fetchHaloLevel = async params =>
  fetchData('GET', '/api/voice/user/userCharmLevel/getInfo', params);

export const fetchUserIcon = async params =>
  fetchData('GET', '/api/voice/user/userLevel/getWebInfo', params);

export const fetchHaloIcon = async params =>
  fetchData('GET', '/api/voice/user/userCharmLevel/getWebInfo', params);

export const fetchUserLevel = async params =>
  fetchData('GET', '/api/voice/user/userLevel/getInfo', params);

export const fetchAristocratList = async params =>
  fetchData('GET', '/api/voice/order/product/aristocrat/listV2', params);
export const fetchAristocratUserInfo = async params =>
  fetchData('GET', '/api/voice/user/snackUser/getUserInfoById', params);
export const finishAristocratCoinGet = async params =>
  fetchData('POST', '/api/voice/user/snackUser/clockIn', params);

export const fetchVipUserStatus = async params =>
  fetchData('GET', '/api/voice/user/switch/status/get', params);

export const setVipUserStatus = async params =>
  fetchData('POST', '/api/voice/user/switch/status/update', params);

export const apolloConfig = async params => {
  return await axios.post('/api/message/snackMessage/getApolloConfigByList', params.data);
};

export const getDiamondToMoneyProductList = async params =>
  fetchData('GET', '/api/order/purchase/diamondToMoneyProduct/list', params);

export const getUserAccount = async params =>
  fetchData('GET', '/api/user/userAccount/income', params);

export const fetchRankingList = async params =>
  fetchData('GET', '/api/voice/content/room/membersRank', params);

export const fullTaskInfo = async params => fetchData('GET', '/api/user/task/fullTaskInfo', params);

export const getTaskList = async params =>
  fetchData('GET', '/api/user/task/list', params, {
    headers: {
      appPlatform: getValueFromUA('appPlatform') === 'android' ? 1 : 2
    }
  });

export const getLimitedTaskList = async params =>
  fetchData('GET', '/api/activity/limitedTask/info', params);
export const getLimitedTaskReward = async params =>
  fetchData('POST', '/api/activity/limitedTask/reward', params);

export const luckyGift = async params => fetchData('GET', '/api/voice/user/luckyGift/rank', params);

export const channelBind = async params =>
  fetchData('POST', '/api/order/payout/channelBind', params, {
    headers: {
      platform,
      appPlatform
    }
  });

export const channelList = async params =>
  fetchData('GET', '/api/order/payout/channelList', params, {
    headers: {
      platform,
      appPlatform
    }
  });

export const channelUnbind = async params =>
  fetchData('GET', '/api/order/payout/channelUnbind', params, {
    headers: {
      platform,
      appPlatform
    }
  });

export const channelBindConfig = async params =>
  fetchData('GET', '/api/order/payout/channelBindConfig', params, {
    headers: {
      platform,
      appPlatform
    }
  });

export const tvStatus = async params => fetchData('GET', '/api/user/tvWall/status/modify', params);

export const tvConfig = async params => fetchData('GET', '/api/user/config/info', params);

export const officialPayInfo = async params =>
  fetchData('GET', '/api/order/payChannel/official/channelInfo', params);

export const officialPayChannel = async params =>
  fetchData('GET', '/api/order/payChannel/official/list/v6', params);

export const coinList = async params =>
  fetchData('GET', '/api/order/product/official/coin/sort/list', params);

export const orderInit = async params =>
  fetchData('POST', '/api/order/wap/init', params, {
    headers: {
      appPlatform: 3
    }
  });

export const rechargeResult = async params => fetchData('GET', '/api/order/rechargeResult', params);

export const airtelPay = async params => fetchData('GET', '/api/order/purchase/airtel/pay', params);

export const airtelCheck = async params =>
  fetchData('GET', '/api/order/purchase/airtel/check', params);

export const signInList = async params =>
  fetchData('GET', '/api/content/signIn/list', params, {
    headers: {
      appPlatform
    }
  });
export const couponRecommend = async params => {
  const curParams = { orderId: params.orderId, refer: params.refer, userId: params.userId, sig: params.sig };
  return fetchData('GET', '/api/order/coupon/recommend', curParams, {
    headers: {
      Authorization: params.token,
    },
  });
};

export const goodlucky = async params => fetchData('GET', '/api/game/games/goodlucky/list', params);

export const getFamilyList = async params => fetchData('GET', '/api/voice/user/family/task', params);

export const getFamilyReceive = async params => fetchData('GET', '/api/voice/user/family/receiveTaskReward', params);

export const getFamilyReport = async params => fetchData('GET', '/api/voice/user/family/receive', params);

export const getFamilyRank = async params => fetchData('GET', '/api/voice/user/family/rank', params);

export const followUser = async params => fetchData('POST', '/api/user/snackUser/follow', params);

export const apolloConfigGame = async params => fetchData('GET', '/api/game/games/rankConfig', params);

export const airWallexToken = async (params, userId) =>
  fetchData('GET', '/api/order/wap/airWallex/token', params, {
    headers: {
      userId,
    },
  });

export const validation = async params => fetchData('POST', '/api/order/forter/validation', params);

export const mpesaPay = async params => fetchData('GET', '/api/order/purchase/mpesa/pay', params);

export const mpesaCheck = async params => fetchData('GET', '/api/order/purchase/mpesa/check', params);

export const receiveList = async params => fetchData('GET', '/api/voice/content/room/giftRank', params);

// 提现
export const incomeShowInfo = async params => fetchDataWithEncrypt('GET', '/api/user/dynamic/assign/userAccount/revenueDisplayDetails', params);

export const productList = async params => fetchDataWithEncrypt('GET', '/api/order/dynamic/assign/payout/item/list', params);

export const getDiamondExchangeInfo = async params => fetchDataWithEncrypt('GET', '/api/user/dynamic/assign/userAccount/retrieveGemSwapInfo', params);

export const diamondExchangeCoins = async params => fetchDataWithEncrypt('POST', '/api/user/dynamic/assign/userAccount/gemSwapTokens', params);

export const recentlyUsed = async params => fetchDataWithEncrypt('GET', '/api/order/dynamic/assign/payout/route/frequentlyAccessed', params);

export const payoutchannelList = async params => fetchDataWithEncrypt('GET', '/api/order/dynamic/assign/payout/routeCollection', params);

export const channelSelect = async params => fetchDataWithEncrypt('GET', '/api/order/dynamic/assign/payout/route/chosen', params);

export const payoutSubmit = async params => fetchDataWithEncrypt('POST', '/api/order/dynamic/assign/payout/send', params);

export const recordList = async params => fetchDataWithEncrypt('GET', '/api/order/dynamic/assign/payout/report/list', params);

export const getShowTask = async params => fetchDataWithEncrypt('GET', '/api/user/dynamic/assign/task/meTask/list', params);

export const getGuidanceTask = async params =>
  fetchDataWithEncrypt('GET', '/api/user/anchor/guidanceTask', params);

export const getAnchorLevelInfo = async params =>
  fetchDataWithEncrypt('GET', '/api/user/anchor/levelInfo', params);

// 官网充值
export const rechargeAward = async params => fetchData('POST', '/api/order/payChannel/official/rechargeAward/receive', params);

export const customizeList = async params => fetchData('GET', '/api/user/customize/list', params);

export const customizeDaysum = async params => fetchData('GET', '/api/user/customize/daysum', params);

export const customizeRewards = async params => fetchData('GET', '/api/user/customize/rewards', params);

export const chatList = async params => fetchData('GET', '/api/message/customize/list', params);

export const getUrl = async params => axios.post('/api/message/snackMessage/getApolloConfigByList', params);

export const diamondToMoneyRatio = async params => fetchData('GET', '/api/message/diamondIncome/diamondToMoneyRatio', params);

export const getRecommendList = async params => fetchData('GET', '/api/voice/user/family/getRecommendListV2', params);

export const bannerList = async params => fetchData('GET', '/api/voice/content/index/banner', params);

export const familyJoin = async params => fetchData('POST', '/api/voice/user/family/join', params);

export const getJoinFamilyInfo = async params => fetchData('GET', '/api/voice/user/family/getJoinFamilyInfo', params);

export const specialOfferList = async params => fetchData('GET', '/api/order/payChannel/official/specialOfferList', params);
